/**
 * Required-add-on gate.
 *
 * Pure function deciding whether the Enhance step's Continue button
 * is allowed to advance. The base service can mark one or more add-ons
 * as `isRequired` (mirrored from Meevo's `numberOfRequiredAddons ≥ 1`
 * config — e.g. "Builder Gel Fill requires Tier 1/2/3/4 Design, One
 * Color/No Design, or Soak Off"). Gate is satisfied when EITHER no
 * required add-ons are configured OR the user has selected at least
 * one of the configured required ones.
 *
 * Why "any one" (not "all"): Meevo's data model is "pick at least 1
 * of the indicated add-ons" — the required list is an OR-group, not
 * an AND-list. If a salon ever needs an AND-group (multiple distinct
 * required selections, e.g. "must pick a design tier AND a color"),
 * that's a separate feature — add an explicit group concept and
 * widen this function to accept it.
 *
 * Same function powers both the RunningBar's `disabled` state (in
 * `App.tsx`) and the Enhance step's subtitle copy + banner (in
 * `Enhance.tsx`). Single source of truth — keeps the two from
 * drifting under future copy or layout tweaks.
 */
export interface RequiredAddOnGateInput {
  readonly addOns: ReadonlyArray<{
    readonly serviceId: string;
    readonly isRequired: boolean;
    /** Single-select group. `null`/absent = standalone (legacy OR-bucket). */
    readonly groupName?: string | null;
  }>;
  readonly selectedAddOnIds: ReadonlyArray<string>;
}

/**
 * Satisfied when EVERY required "bucket" has at least one selection. Required
 * add-ons are bucketed by `groupName`:
 *   - Each named group is its own bucket. Because the widget renders a group as
 *     single-select radios, "≥1 selected" is effectively "exactly one" — this is
 *     Lotus's "pick one Post Color finish" case. Multiple distinct required
 *     groups must ALL be satisfied (AND across groups).
 *   - Ungrouped required add-ons (groupName null) share ONE bucket, preserving
 *     the legacy Meevo behavior: "pick at least one of the required add-ons."
 */
export function isRequiredAddOnGateSatisfied(
  input: RequiredAddOnGateInput,
): boolean {
  const required = input.addOns.filter((a) => a.isRequired);
  if (required.length === 0) return true;

  const buckets = new Map<string | null, string[]>();
  for (const a of required) {
    const key = a.groupName ?? null;
    const ids = buckets.get(key) ?? [];
    ids.push(a.serviceId);
    buckets.set(key, ids);
  }

  for (const ids of buckets.values()) {
    if (!ids.some((id) => input.selectedAddOnIds.includes(id))) return false;
  }
  return true;
}

export function hasRequiredAddOns(
  addOns: ReadonlyArray<{ readonly isRequired: boolean }>,
): boolean {
  return addOns.some((a) => a.isRequired);
}
